// components/VirtualKeyboard.jsx
// Composant réutilisable pour le clavier virtuel avec caractères spéciaux
// Utilisé par : ConjugationExercise, ConjugationCompleteExercise, VocabularyExercise

import React from 'react';
import { useTranslation } from 'react-i18next';
import { getSpecialChars, hasSpecialChars } from '../utils/exerciseUtils';
import '../components/VirtualKeyboard.css';

/**
 * Composant VirtualKeyboard
 * Affiche un clavier virtuel avec les caractères spéciaux d'une langue
 *
 * @param {Object} props
 * @param {string} props.langue - Code langue (fr, en, es)
 * @param {boolean} props.checkAccents - Si true, le clavier est affiché
 * @param {Function} props.onCheckAccentsChange - Callback quand la checkbox change
 * @param {Function} props.onCharClick - Callback quand un caractère est cliqué
 * @param {boolean} props.showCheckbox - Si true, affiche la checkbox "Vérifier les accents" (défaut: true)
 * @param {string} props.checkboxLabel - Label de la checkbox (défaut: traduction de virtualKeyboard.verifierAccents)
 */
export default function VirtualKeyboard({
  langue,
  checkAccents,
  onCheckAccentsChange,
  onCharClick,
  showCheckbox = true,
  checkboxLabel,
}) {
  const { t } = useTranslation();

  // Ne rien afficher si la langue n'a pas de caractères spéciaux
  if (!hasSpecialChars(langue)) {
    return null;
  }

  const specialChars = getSpecialChars(langue);
  const label = checkboxLabel ?? t('virtualKeyboard.verifierAccents');

  return (
    <div className="language-helpers">
      {/* Clavier masqué : checkbox avec label */}
      {showCheckbox && !checkAccents && (
        <label className="checkbox-label">
          <input type="checkbox" checked={checkAccents} onChange={(e) => onCheckAccentsChange(e.target.checked)} />
          {label}
        </label>
      )}

      {/* Clavier affiché : checkbox inline avec les touches, sans texte */}
      {checkAccents && specialChars.length > 0 && (
        <div className="virtual-keyboard">
          {showCheckbox && (
            <label className="checkbox-label-inline">
              <input type="checkbox" checked={checkAccents} onChange={(e) => onCheckAccentsChange(e.target.checked)} />
            </label>
          )}
          {specialChars.map((char) => (
            <button
              key={char}
              type="button"
              className="special-char-btn"
              onMouseDown={(e) => e.preventDefault()} // Empêche la perte de focus
              onClick={() => onCharClick(char)}
            >
              {char}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
